import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Sidebar } from '@/components/layout/sidebar';
import { TopBar } from '@/components/layout/top-bar';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { useRealtimeBridge } from '@/hooks/use-realtime';
import { onSessionExpired } from '@/lib/api/client';
import { useAuthStore } from '@/stores/auth-store';

/**
 * Owns the two things that must exist exactly once per session: the socket
 * bridge and the "your session ended" handler. The sidebar and top bar never
 * remount, so route content is the only thing that animates.
 */
export function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const expire = useAuthStore((s) => s.expire);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useRealtimeBridge();

  useEffect(
    () =>
      onSessionExpired(() => {
        expire();
        toast.error('Session expired', { description: 'Please sign in again.' });
        navigate('/login', { replace: true, state: { from: location.pathname } });
      }),
    // location is read at fire time only; re-subscribing per navigation would
    // drop the listener between renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [expire, navigate],
  );

  // The live map manages its own scrolling; every other route scrolls normally.
  const isMap = location.pathname.startsWith('/map');

  return (
    <div className="flex h-dvh overflow-hidden bg-background">
      <div className="hidden lg:block">
        <Sidebar />
      </div>

      {/* Below lg the rail becomes a sheet rather than disappearing. */}
      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent side="left" className="w-[268px] p-0" showCloseButton={false}>
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <Sidebar onNavigate={() => setMobileNavOpen(false)} forceExpanded />
        </SheetContent>
      </Sheet>

      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar onOpenSidebar={() => setMobileNavOpen(true)} />

        <main className="relative min-h-0 flex-1">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={isMap ? 'map' : location.pathname}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.16, ease: [0.2, 0.8, 0.2, 1] }}
              className={isMap ? 'absolute inset-0' : 'h-full overflow-y-auto'}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
}
